import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  CalendarPlus,
  ChevronDown,
  ChevronRight,
  Download,
  Play,
  Trash2,
  Undo2,
} from "lucide-react";
import { useDataProvider, useNotify } from "ra-core";
import { useCallback, useEffect, useState } from "react";

import { Explain } from "./Explain";
import { SendDialog } from "./SendDialog";
import { SendOneCard } from "./SendOneCard";
import type { Vendor } from "./sendClient";
import {
  blockedReason,
  buildManifestCsv,
  dropRpcViaSupabase,
  groupDropsByDate,
  isRefusal,
  manifestFilename,
  planDrop,
  planRemoval,
  scheduleErrorMessage,
  unplanDrop,
} from "./dropsRules";

/**
 * Drops — schedule, plan and send, in one place.
 *
 * WHY THESE WERE MERGED
 * ---------------------
 * Schedule and Drops were separate screens because they were separate tables:
 * `drop_schedule` held the intention, `mailings` held the rows. They are two
 * stages of ONE action. Keeping them apart meant a date on one screen and a
 * count on another, with nothing showing whether they agreed -- and a drop
 * planned without being scheduled appeared on neither.
 *
 * `drop_board` is now the single source: a FULL OUTER JOIN, so a drop shows up
 * if it was scheduled, or if it exists in mailings, or both. Nothing mailable
 * is off this board.
 *
 * THE ORDER OF OPERATIONS IS THE SCREEN'S STRUCTURE
 * -------------------------------------------------
 *     schedule  ->  plan  ->  send
 *     a date        rows      postage
 *
 * Each step is deliberate and each is reversible until the last one, which is
 * not reversible at all. The row shows which step it is on, and offers only
 * that step's action -- so "plan" and "send" are never two adjacent buttons
 * that differ by a word.
 */

type Row = {
  id: string;
  cell: string;
  touch: string;
  schedule_id: number | null;
  planned_on: string | null;
  schedule_status: string | null;
  notes: string | null;
  vendor: Vendor | null;
  first_mail_date: string | null;
  creative_name: string | null;
  creative_status: string | null;
  artwork_ready: boolean | null;
  doors_in_cell: number;
  mailings_planned: number;
  mailings_sent: number;
  // No "unscheduled" state. A drop planned outside the schedule has
  // schedule_id null -- that is a property, not a stage, and the board groups
  // those under their own heading. 025 had it as a state; it was unreachable,
  // and 026 removed it.
  state:
    | "upcoming"
    | "overdue"
    | "ready to send"
    | "part sent"
    | "sent"
    | "skipped";
};

const CELLS = Array.from(
  { length: 18 },
  (_, i) => `C${String(i + 1).padStart(2, "0")}`,
);
const TOUCHES = Array.from(
  { length: 6 },
  (_, i) => `T${String(i + 1).padStart(2, "0")}`,
);

/** Color carries meaning, so every state also carries a word. */
const STATE: Record<Row["state"], string> = {
  overdue: "border-destructive text-destructive",
  "ready to send": "border-foreground",
  "part sent": "border-foreground",
  sent: "text-muted-foreground",
  skipped: "text-muted-foreground line-through",
  upcoming: "text-muted-foreground",
};

const fmt = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

export const Drops = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [busy, setBusy] = useState(false);

  const [cell, setCell] = useState("C01");
  const [touch, setTouch] = useState("T01");
  const [when, setWhen] = useState("");
  const [vendor, setVendor] = useState("none");

  const load = useCallback(async () => {
    const { data } = await dataProvider.getList("drop_board", {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "planned_on", order: "ASC" },
      filter: {},
    });
    setRows(data as Row[]);
  }, [dataProvider]);

  useEffect(() => {
    load().catch((e) => notify(String(e?.message ?? e), { type: "error" }));
  }, [load, notify]);

  const schedule = async () => {
    if (!when) return notify("Pick a date first", { type: "warning" });
    setBusy(true);
    try {
      await dataProvider.create("drop_schedule", {
        data: {
          cell,
          touch,
          planned_on: when,
          // "none" is the UI saying undecided. Storing null rather than a
          // default printer matters: guessing one is how postage gets bought at
          // the wrong price.
          vendor: vendor === "none" ? null : vendor,
        },
      });
      await load();
      notify(`${cell} · ${touch} scheduled`, { type: "info" });
    } catch (e) {
      notify(scheduleErrorMessage(e, cell, touch), { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  if (!rows) return <Skeleton className="h-40 w-full" />;

  // Grouped by date, because the question is "what goes out that week". Drops
  // that exist without a date are grouped first rather than hidden: an unplanned
  // date is a gap someone should see, not a reason to omit the row.
  const groups = groupDropsByDate(rows);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Drops</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          One drop is one <Explain term="cell">cell</Explain> receiving one{" "}
          <Explain term="touch">postcard</Explain>. Give it a date, turn it into
          cards, then send them. Nothing here posts anything until the last
          step, and that step says so plainly.
        </p>
      </div>

      <Card>
        <CardContent className="pt-4 flex flex-wrap items-end gap-2">
          <Picker
            label="Cell"
            value={cell}
            onChange={setCell}
            options={CELLS}
          />
          <Picker
            label="Touch"
            value={touch}
            onChange={setTouch}
            options={TOUCHES}
          />
          <div>
            <div className="text-xs text-muted-foreground mb-1">Drop date</div>
            <Input
              type="date"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
              className="w-44"
            />
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Printer</div>
            <Select value={vendor} onValueChange={setVendor}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not decided</SelectItem>
                <SelectItem value="stannp">Stannp</SelectItem>
                <SelectItem value="pcm">PCM</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={schedule} disabled={busy}>
            <CalendarPlus className="size-4" /> Schedule
          </Button>
        </CardContent>
      </Card>

      {!groups.length ? (
        <p className="text-sm text-muted-foreground">
          Nothing scheduled or planned yet. The holdout is deliberately absent —
          it cannot be scheduled at all, which is why it cannot be mailed by
          accident.
        </p>
      ) : (
        groups.map(([key, list]) => (
          <div key={key} className="space-y-2">
            <div className="text-sm font-medium sticky top-0 bg-background py-1">
              {key === "unscheduled" ? (
                <>
                  No date
                  <span className="text-muted-foreground font-normal">
                    {" "}
                    · planned outside the schedule
                  </span>
                </>
              ) : (
                fmt(key)
              )}
              <span className="text-muted-foreground font-normal">
                {" "}
                · {list.length} drop{list.length === 1 ? "" : "s"}
              </span>
            </div>
            {list.map((r) => (
              <DropRow key={r.id} row={r} onChanged={load} />
            ))}
          </div>
        ))
      )}

      <SendOneCard onSent={load} />
    </div>
  );
};

const Picker = ({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) => (
  <div>
    <div className="text-xs text-muted-foreground mb-1">{label}</div>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-28">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {/* HOLDOUT is deliberately absent. plan_drop() would refuse it anyway;
            not offering it means never having to explain the refusal. */}
        {options.map((o) => (
          <SelectItem key={o} value={o}>
            {o}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

type Attempt = {
  id: number;
  vendor: string;
  mode: string;
  environment: string | null;
  attempted: number;
  accepted: number;
  recorded: number;
  vendor_order_id: string | null;
  vendor_batch_id: string | null;
  ok: boolean;
  error: string | null;
  attempted_at: string;
  attempted_by_name: string | null;
};

type ManifestRow = {
  mailing_id: number;
  addressee: string | null;
  street: string;
  city: string | null;
  state: string | null;
  zip5: string;
  recipient_ref: string;
  status: string;
};

/**
 * One drop, and the single next thing to do with it.
 *
 * Collapsed it reads as a status line. Opened it offers exactly one action:
 * unplanned drops can be planned, planned drops can be sent, sent drops can
 * only be read. Showing Plan and Send side by side would put an irreversible
 * button next to a reversible one that looks just like it.
 */
const DropRow = ({
  row,
  onChanged,
}: {
  row: Row;
  onChanged: () => Promise<void>;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<number | null>(null);
  const [manifest, setManifest] = useState<ManifestRow[] | null>(null);
  const [attempt, setAttempt] = useState<Attempt | null>(null);
  const [inspecting, setInspecting] = useState(false);
  const [inspected, setInspected] = useState<Record<string, unknown> | null>(
    null,
  );

  /**
   * The last time somebody pressed the button on this drop.
   *
   * A sandbox order changes nothing in `mailings` -- correctly, since it was
   * never processed -- so without this the row looks identical before and after
   * a rehearsal, and "did I already test this?" has no answer on screen.
   */
  const loadAttempt = useCallback(async () => {
    try {
      const { data } = await dataProvider.getList("send_attempt_latest", {
        filter: { cell: row.cell, touch: row.touch },
        pagination: { page: 1, perPage: 1 },
        sort: { field: "attempted_at", order: "DESC" },
      });
      setAttempt((data as Attempt[])[0] ?? null);
    } catch {
      setAttempt(null);
    }
  }, [dataProvider, row.cell, row.touch]);

  const loadManifest = useCallback(async () => {
    const { data } = await dataProvider.getList("drop_manifest", {
      filter: { cell: row.cell, touch: row.touch },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "mailing_id", order: "ASC" },
    });
    setManifest(data as ManifestRow[]);
  }, [dataProvider, row.cell, row.touch]);

  useEffect(() => {
    if (open && manifest === null) loadManifest().catch(() => setManifest([]));
  }, [open, manifest, loadManifest]);

  // Loaded for every row, not only expanded ones: whether a drop has been
  // rehearsed is exactly the sort of thing you want to see without opening it.
  useEffect(() => {
    loadAttempt();
  }, [loadAttempt, row.mailings_planned, row.mailings_sent]);

  const plan = async (dryRun: boolean) => {
    setBusy(true);
    try {
      const planned = await planDrop(
        dropRpcViaSupabase,
        { cell: row.cell, touch: row.touch, vendor: row.vendor },
        dryRun,
      );
      if (dryRun) {
        setPreview(planned);
      } else {
        notify(`Planned ${planned} cards for ${row.cell} ${row.touch}`, {
          type: "info",
        });
        setPreview(null);
        setManifest(null);
        await onChanged();
      }
    } catch (e) {
      // plan_drop raises for HOLDOUT, a malformed touch or an unknown cell.
      // Surfacing the database's own message keeps ONE explanation rather than
      // a paraphrase that drifts from it.
      notify((e as Error).message, { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const unplan = async () => {
    setBusy(true);
    try {
      const removed = await unplanDrop(dropRpcViaSupabase, row.cell, row.touch);
      notify(
        `Removed ${removed} planned card(s). Anything already sent was kept.`,
        {
          type: "info",
        },
      );
      setManifest(null);
      await onChanged();
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  /**
   * Remove the whole drop, whichever halves of it exist.
   *
   * The old control only appeared when a drop had a schedule row AND no planned
   * cards -- which is the one combination a person is least likely to want to
   * remove. A drop planned outside the schedule (no schedule_id, one card) had
   * no removal in the header at all, and the only way out was `Unplan` hidden
   * behind the expander. Two halves, two buttons, neither visible when both
   * halves were present.
   *
   * One action, and it does whatever this row actually consists of: unplan the
   * cards, delete the schedule row, or both. Sent cards stop it outright --
   * postage was spent, and a ledger that can be quietly edited is not a ledger.
   */
  const removeDrop = async () => {
    const plan = planRemoval(row);
    if (isRefusal(plan)) {
      notify(plan.refuse, { type: "warning" });
      return;
    }
    setBusy(true);
    try {
      if (plan.unplan) {
        await unplanDrop(dropRpcViaSupabase, row.cell, row.touch);
      }
      if (plan.deleteSchedule && row.schedule_id) {
        await dataProvider.delete("drop_schedule", { id: row.schedule_id });
      }
      notify(`${row.cell} · ${row.touch} removed.`, { type: "info" });
      setManifest(null);
      await onChanged();
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  /**
   * Ask PCM what it actually recorded for that order.
   *
   * Two things were guesses until this existed: the undocumented `size` key we
   * send, and whether PCM ever fetched the signed artwork URL before it
   * expired. `productType` answers the first in their own vocabulary; a
   * non-null `designID` answers the second, because they only get one by
   * ingesting the file.
   */
  const inspect = async () => {
    if (!attempt?.vendor_order_id) return;
    setInspecting(true);
    try {
      const { getSupabaseClient } = await import(
        "../providers/supabase/supabase"
      );
      const { data, error } = await getSupabaseClient().functions.invoke(
        "pcm-order",
        {
          body: {
            order_id: attempt.vendor_order_id,
            env: attempt.environment ?? "sandbox",
          },
        },
      );
      if (error) {
        const ctx = (error as { context?: Response }).context;
        const b = ctx?.json ? await ctx.json().catch(() => null) : null;
        throw new Error(b?.error ?? error.message);
      }
      setInspected(
        (data as { answers?: Record<string, unknown> })?.answers ?? {},
      );
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    } finally {
      setInspecting(false);
    }
  };

  const downloadCsv = () => {
    const body = buildManifestCsv(manifest);
    const url = URL.createObjectURL(new Blob([body], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = manifestFilename(row.cell, row.touch);
    a.click();
    URL.revokeObjectURL(url);
  };

  // Why this drop cannot be sent, in the order the sender checks them. One
  // reason at a time: a list of four problems is read as "this is broken",
  // whereas one is read as "do this next".
  const blocked = blockedReason(row);

  return (
    <Card>
      <CardContent className="pt-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => setOpen((o) => !o)}
            className="flex items-center gap-1 font-mono text-sm font-medium hover:underline"
          >
            {open ? (
              <ChevronDown className="size-4" />
            ) : (
              <ChevronRight className="size-4" />
            )}
            {row.cell} · {row.touch}
          </button>
          <Badge variant="outline" className={cn(STATE[row.state])}>
            {row.state}
          </Badge>
          {/*
            WHAT WILL ACTUALLY BE SENT COMES FIRST.

            This line used to lead with the cell size -- "283 doors" -- beside a
            quiet "1 planned". The most prominent number was therefore the one
            that would NOT be sent, sitting next to a Send button that reads as
            a promise about the click. A cell has 283 doors whether or not
            anybody planned any of them; only the planned count is actionable.
          */}
          {row.mailings_planned > 0 ? (
            <span className="text-sm">
              <b>
                {row.mailings_planned.toLocaleString()} card
                {row.mailings_planned === 1 ? "" : "s"}
              </b>{" "}
              ready
              <span className="text-muted-foreground">
                {" "}
                · {row.doors_in_cell.toLocaleString()} in cell
              </span>
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">
              {row.doors_in_cell.toLocaleString()} doors in cell
            </span>
          )}
          <span className="text-sm text-muted-foreground">
            {row.creative_name ? (
              <>
                {row.creative_name}
                {row.creative_status !== "approved"
                  ? ` (${row.creative_status})`
                  : ""}
              </>
            ) : (
              // Worth saying loudly: a drop with no artwork cannot go, and the
              // date will arrive regardless.
              <span className="text-destructive">
                no creative for {row.touch}
              </span>
            )}
          </span>
          <span className="text-xs text-muted-foreground">
            {row.vendor ?? "printer not decided"}
          </span>
          {/* Planned is stated once, above. Only what is already SENT adds
              anything here, and it is history rather than a target. */}
          {row.mailings_sent ? (
            <span className="text-xs text-muted-foreground tabular-nums">
              {row.mailings_sent.toLocaleString()} already sent
            </span>
          ) : null}

          <div className="ml-auto flex items-center gap-2">
            {/*
              THE NEXT STEP, ON THE ROW.

              A scheduled drop with nothing planned showed no action at all --
              its badge said "upcoming" and the Plan button was inside the
              expander, so the row looked finished and the sequence stalled
              silently. "Send or test" then never appeared, because there was
              genuinely nothing to send, which read as a missing button rather
              than a missing step.

              Planning writes rows and spends nothing, and Remove undoes it in
              one click, so it does not need the preview-then-confirm the
              expander offers. The irreversible step is Send, and that keeps
              every one of its guards.
            */}
            {row.mailings_planned === 0 && row.mailings_sent === 0 && (
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => plan(false)}
              >
                <Play className="size-4" /> Plan{" "}
                {row.doors_in_cell.toLocaleString()} cards
              </Button>
            )}
            {row.mailings_planned > 0 && (
              <SendDialog
                cell={row.cell}
                touch={row.touch}
                count={row.mailings_planned}
                vendor={row.vendor}
                blocked={blocked}
                onDone={onChanged}
              />
            )}
            {row.mailings_sent === 0 && (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Remove this drop"
                title={
                  row.mailings_planned > 0 && row.schedule_id
                    ? `Remove this drop entirely: unplan ${row.mailings_planned} card(s) AND drop the scheduled date`
                    : row.mailings_planned > 0
                      ? `Unplan ${row.mailings_planned} card(s). This drop has no scheduled date to remove.`
                      : "Remove the scheduled date. Nothing is planned."
                }
                disabled={busy}
                onClick={removeDrop}
              >
                <Trash2 className="size-4" />
              </Button>
            )}
          </div>
        </div>

        {/*
          THE LAST ATTEMPT, ON THE COLLAPSED ROW.

          A rehearsal leaves `mailings` untouched by design, so without this the
          row looks identical before and after one and "have I already tested
          this?" is unanswerable from the screen. Shown collapsed because that
          is when the question gets asked.
        */}
        {attempt && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground border-t pt-2">
            <Badge
              variant={attempt.ok ? "outline" : "destructive"}
              className={attempt.mode === "test" ? "" : "border-foreground"}
            >
              {attempt.mode === "test"
                ? `${attempt.vendor} ${attempt.environment ?? "test"} rehearsal`
                : `${attempt.vendor} live send`}
            </Badge>
            <span>
              {attempt.accepted} of {attempt.attempted} accepted
              {attempt.mode === "live" ? ` · ${attempt.recorded} recorded` : ""}
            </span>
            {attempt.vendor_order_id && (
              <span className="font-mono">order {attempt.vendor_order_id}</span>
            )}
            <span>{new Date(attempt.attempted_at).toLocaleString()}</span>
            {attempt.attempted_by_name && (
              <span>by {attempt.attempted_by_name}</span>
            )}
            {attempt.error && (
              <span className="text-destructive">{attempt.error}</span>
            )}
            {attempt.vendor === "pcm" && attempt.vendor_order_id && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2"
                disabled={inspecting}
                onClick={inspect}
              >
                {inspecting ? "Checking…" : "Check with PCM"}
              </Button>
            )}
          </div>
        )}

        {inspected && (
          <div className="text-xs border rounded-md p-2 space-y-1">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">
                status: {String(inspected.status ?? "—")}
              </Badge>
              {/* The undocumented size key, in PCM's own words. */}
              <Badge variant="outline">
                product: {String(inspected.product_type ?? "—")}
              </Badge>
              <Badge variant="outline">
                {String(inspected.mail_class ?? "—")}
              </Badge>
              {/* A design id only exists if PCM fetched the signed URL. */}
              <Badge
                variant={
                  inspected.artwork_ingested ? "secondary" : "destructive"
                }
              >
                {inspected.artwork_ingested
                  ? `artwork ingested (design ${inspected.design_id})`
                  : "PCM has no artwork for this order"}
              </Badge>
            </div>
            {typeof inspected.proof === "string" && inspected.proof && (
              <a
                href={inspected.proof}
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                PCM's own proof of the print file
              </a>
            )}
          </div>
        )}

        {open && (
          <div className="border-t pt-3 space-y-3">
            {row.mailings_planned === 0 && row.mailings_sent === 0 ? (
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => plan(true)}
                >
                  Preview
                </Button>
                <Button
                  size="sm"
                  disabled={busy || preview === null || preview === 0}
                  onClick={() => plan(false)}
                >
                  <Play className="size-4" /> Plan {preview ?? ""} cards
                </Button>
                {preview !== null && (
                  <Badge variant={preview ? "secondary" : "outline"}>
                    {preview} to plan
                  </Badge>
                )}
                <p className="text-xs text-muted-foreground basis-full">
                  Planning writes rows; it posts nothing. Opt-outs and retired
                  doors are excluded automatically, and the holdout can never be
                  selected.
                </p>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadCsv}
                  disabled={!manifest?.length}
                >
                  <Download className="size-4" /> Manifest CSV
                </Button>
                {/*
                  UNPLAN ONLY EXISTS WHEN IT DIFFERS FROM THE BIN.

                  The two do the same thing on an unscheduled drop -- both call
                  unplan_drop and stop -- so the row offered two controls with
                  one effect, which invites the reasonable conclusion that one
                  of them does something else.

                  With a scheduled date they genuinely differ, and the labels
                  now say how:
                      Unplan  removes the cards, KEEPS the date
                      Bin     removes the whole drop, date included
                */}
                {row.mailings_planned > 0 && row.schedule_id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={unplan}
                    title="Discard the planned cards but keep this drop's scheduled date"
                  >
                    <Undo2 className="size-4" /> Unplan {row.mailings_planned},
                    keep the date
                  </Button>
                )}
                {row.mailings_sent > 0 && (
                  <p className="text-xs text-muted-foreground basis-full">
                    Unplan only removes cards still <b>planned</b>. Anything
                    sent is history — postage was spent, and a ledger that can
                    be quietly edited is not a ledger.
                  </p>
                )}
              </div>
            )}

            {manifest === null ? (
              <Skeleton className="h-24 w-full" />
            ) : manifest.length ? (
              <>
                <div className="border rounded-md divide-y max-h-72 overflow-y-auto">
                  {manifest.slice(0, 200).map((m) => (
                    <div
                      key={m.mailing_id}
                      className="p-2 text-sm flex justify-between gap-3"
                    >
                      <div>
                        <div className="font-medium">{m.addressee ?? "—"}</div>
                        <div className="text-muted-foreground text-xs">
                          {m.street}, {m.city} {m.state} {m.zip5}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <Badge
                          variant={
                            m.status === "planned" ? "outline" : "secondary"
                          }
                        >
                          {m.status}
                        </Badge>
                        <div className="text-xs text-muted-foreground font-mono mt-1">
                          {m.recipient_ref}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {manifest.length > 200 && (
                  <p className="text-xs text-muted-foreground">
                    Showing 200 of {manifest.length}. The CSV contains all of
                    them.
                  </p>
                )}
              </>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
